/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef } from "react";
import { InputLabel, TextField, TextFieldProps } from "@mui/material";
import { useStyle } from "./style";
import { EIdFormat, formatId } from "@utils/idFormat";
import { cls } from "itsf-ui-common";
import { ErrorHelperText } from "@common/ErrorHelperText/ErrorHelperText";

interface IParams {
    labelText: string;
    isRequired?: boolean;
    isReadOnly?: boolean;
}

export const LabeledTextField = forwardRef<HTMLDivElement, IParams & Omit<TextFieldProps, "label" | "aria-label">>(
    (props, ref) => {
        const { labelText, isRequired, id: providedId, className, isReadOnly = false, ...textFieldProps } = props;
        const { classes } = useStyle();

        const id = providedId ?? formatId(labelText, EIdFormat.FIELD);
        const labelId = `${id}-label`;

        const inputProps = {
            ...textFieldProps.inputProps,
            "aria-labelledby": labelId,
        };

        const getHelperText = () => {
            if (!textFieldProps.helperText) {
                return undefined;
            }

            if (textFieldProps.error) {
                return <ErrorHelperText message={textFieldProps.helperText as string} />;
            }

            return textFieldProps.helperText;
        };

        return (
            <div>
                <InputLabel id={labelId}>
                    {labelText}
                    {isRequired && <span className={classes.required}> *</span>}
                </InputLabel>
                <TextField
                    {...textFieldProps}
                    FormHelperTextProps={{
                        ...textFieldProps.FormHelperTextProps,
                        className: cls(classes.formHelperText, textFieldProps.FormHelperTextProps?.className),
                    }}
                    className={cls(isReadOnly ? "LabeledTextField-readOnly" : undefined, className)}
                    disabled={Boolean(textFieldProps.disabled ?? isReadOnly)}
                    helperText={getHelperText()}
                    id={id}
                    inputProps={inputProps}
                    ref={ref}
                />
            </div>
        );
    }
);

LabeledTextField.displayName = "LabeledTextField";
